import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import useFetch from "../../../hooks/useFetch";
import { useAuth } from "../../../hooks/authContext";
import { API } from "../../../common/Constants";
import { 
    setAuthToken, 
    setRefreshAuthToken, 
    setTenantID , 
    setRememberMe as storeRememebrMe, 
    getRememberMe
} from "../../../common/Utils";
import PreLoader from "../../../core/common/preLoader";
import { useAuthTenant } from "../../../hooks/useAuthTenant";
import HeaderLogo from "../../../core/common/headerLogo";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import { InputOtp } from "primereact/inputotp";

type PasswordField = "password";

const Login = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const [inProgress, setProgress] = useState<boolean>(false);
  const {setAuthUser, setIsLoggedIn} = useAuth();
  const [searchParams] = useSearchParams();
  const { callFetch } = useFetch();

  const [username, setUserName] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const {
    tenant,
    setTenant,
    setTenantResolved,
    hideTenantInput,
  } = useAuthTenant();
  const [rememberMe, setRememberMe]  = useState<boolean>(false || getRememberMe());
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [tenantInfo, setTenantInfo] = useState<{
    institutionName: string;
    institutionLogo: string;
  } | null>(null);

  // OTP Login States
  const [isOTPLogin, setIsOTPLogin] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [otp, setOTP] = useState<any>(undefined);
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(120); // 2 minutes in seconds
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
  const [resendMessage, setResendMessage] = useState<string | undefined>("");

  const fetchTenantInfo = async () => {
    if (!tenant) return;
    setTenantResolved(false);
      callFetch(
        API.TENANT_INFO.replace(':tenantId', tenant),
        'GET',
        {'X-Tenant-ID': tenant},
        null,
        null
      ).then((response) => {
        if (response && response.settingGroups) {
          const brandingGroup = response.settingGroups.find(
            (group: any) => group.category === 'BRANDING'
          );
          
          if (brandingGroup && brandingGroup.settings) {
            const institutionNameSetting = brandingGroup.settings.find(
              (setting: any) => setting.settingKey === 'institution_name'
            );
            const institutionLogoSetting = brandingGroup.settings.find(
              (setting: any) => setting.settingKey === 'institution_logo'
            );
            
            setTenantInfo({
              institutionName: institutionNameSetting?.currentValue || 'Institution',
              institutionLogo: institutionLogoSetting?.currentValue || ''
            });
          }
          setTenantResolved(true);
        }
      }).catch((error) => {
        console.error('Failed to fetch tenant info:', error);
        setTenantResolved(false);
      });
  };

  useEffect(() => {
    if (tenant) {
      fetchTenantInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  // Timer implementation for OTP
  useEffect(() => {
    let interval: any | undefined;
    
    if (isOTPSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, isOTPSent]);

  const reset = () => {
      setUserName(undefined)
      setPassword(undefined)
  }

  const resetOTPForm = () => {
    setPhoneNumber(undefined);
    setOTP(undefined);
    setIsOTPSent(false);
    setTimer(120);
    setIsResendDisabled(true);
    setErrorMessage(undefined);
    setResendMessage("");
  }

  const toggleLoginMethod = () => {
    setIsOTPLogin(!isOTPLogin);
    setErrorMessage(undefined);
    if (isOTPLogin) {
      resetOTPForm();
    }
  }

  const handleSendOTP = (event: any) => {
    event.preventDefault();
    if (!phoneNumber) {
      setErrorMessage("Please provide username");
    } else {
      setErrorMessage(undefined);
      setProgress(true);
      callFetch(API.LOGIN_SEND_OTP,
        'POST',
        {'X-Tenant-ID': tenant},
        null,
        {
          username: phoneNumber,
          otpType: "LOGIN"
        }
      ).then((response) => {
        setProgress(false);
        setIsOTPSent(true);
        setTimer(120);
        setIsResendDisabled(true);
        setErrorMessage(undefined);
      }).catch((error) => {
        setProgress(false);
        if (error) {
          if (error.response?.data) {
            setErrorMessage(error.response?.data);
          } else {
            setErrorMessage('Failed to send OTP. Please try again.');
          }
        }
      });
    }
  }

  const handleResendOTP = (event: any) => {
    event.preventDefault();
    if (isResendDisabled) return;
    
    if (!phoneNumber) {
      setErrorMessage("Please provide username");
      setResendMessage("");
    } else {
      setErrorMessage(undefined);
      setProgress(true);
      callFetch(API.LOGIN_SEND_OTP,
        'POST',
        {'X-Tenant-ID': tenant},
        null,
        {
          username: phoneNumber,
          otpType: "LOGIN"
        }
      ).then((response) => {
        setProgress(false);
        setTimer(120);
        setIsResendDisabled(true);
        setErrorMessage(undefined);
        setResendMessage("OTP resent successfully.");
      }).catch((error) => {
        setProgress(false);
        setResendMessage("");
        if (error) {
          if (error.response?.data) {
            setErrorMessage(error.response?.data);
          } else {
            setErrorMessage('Failed to resend OTP. Please try again.');
          }
        }
      });
    }
  }

  const handleVerifyOTP = (event: any) => {
    event.preventDefault();
    if (!phoneNumber) {
      setErrorMessage("Please provide username");
      setResendMessage("");
    } else if (!otp) {
      setErrorMessage("Please provide OTP");
      setResendMessage("");
    } else {
      setErrorMessage(undefined);
      setResendMessage("");
      setProgress(true);
      callFetch(API.LOGIN_WITH_OTP,
        'POST',
        {'X-Tenant-ID': tenant},
        null,
        {
          username: phoneNumber,
          otp: otp
        }
      ).then((response) => {
        setProgress(false);
        setAuthUser(phoneNumber);
        setIsLoggedIn(true);
        resetOTPForm();
        onSuccess(response);
      }).catch((error) => {
        setProgress(false);
        setResendMessage("");
        if (error) {
          if (error.response?.data) {
            setErrorMessage(error.response?.data);
          } else {
            setErrorMessage('Invalid OTP. Please try again.');
          }
        }
      });
    }
  }

  // Format the timer as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (event:any) => {
    event.preventDefault()
    if(!tenant){
      setErrorMessage("Please provide tenant")
    } else if(!username){
      setErrorMessage("Please provide username")
    } else if(!password){
      setErrorMessage("Please provide password")
    } else {
      setErrorMessage("")
      setProgress(true)
      callFetch(API.LOGIN,
          'POST',
          {'X-Tenant-ID': tenant},
          null,
          {
              username: username,
              password: password
          }
      ).then((response)=>{
        setProgress(false)
        setAuthUser(username);
        setIsLoggedIn(true);
        reset();
        onSuccess(response);
      }).catch((error) => {
        setProgress(false)
        if(error){
          if(error.response?.data){
            setErrorMessage(error.response?.data)
          } else{
            setErrorMessage('Something went wrong please try again later.')
          }
        }
      });
    }
  }

  const onSuccess = (data:any) => {
    setAuthToken(data?.token)  
    if(rememberMe){
      storeRememebrMe(true);
      setRefreshAuthToken(data?.refreshToken)
    } else{
      storeRememebrMe(false);
    }
    setTenantID(tenant)
    navigationPath()
  };

  const navigationPath = () => {
      navigation(searchParams.get('path') || routes.index);
  };

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const renderLoginWithOTP = () => {
    return (
      <>
        {!isOTPSent ? (
          // Step 1: Username Input
          <>
            <div className="mb-3">
              <label className="form-label">Username</label>
              <div className="input-group">
                <input
                  type="text"
                  defaultValue=""
                  placeholder="Enter your username"
                  className="form-control border-end-0"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  value={phoneNumber}
                />
                <span className="input-group-text border-start-0">
                  <i className="ti ti-user" />
                </span>
              </div>
            </div>
            <div className="mb-3">
              {
                <div className="text-center" style={{height:12}}>
                  {
                    errorMessage
                    ?
                      <>
                        <span className="link-danger">
                        <i className="ti ti-alert-triangle" /> {errorMessage}
                        </span>
                      </>
                    :
                      <></>
                  }
                </div>
              }
            </div>
            <div className="mb-3">
              <button
                onClick={handleSendOTP}
                type="submit"
                className="btn btn-primary w-100"
              >
                Send OTP
              </button>
            </div>
            <div className="mb-3">
              <div className="text-center">
                <Link
                  to="#"
                  onClick={toggleLoginMethod}
                  className="text-primary"
                  style={{ textDecoration: 'none' }}
                >
                  <i className="ti ti-arrow-left me-1" />
                  Back to Login with Password
                </Link>
              </div>
            </div>
          </>
        ) : (
          // Step 2: OTP Input
          <>
            <div className="text-center mb-3">
              <p className="text-muted">
                OTP sent to your registered mobile number
              </p>
            </div>
            <div className="text-center otp-input">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <InputOtp value={otp} onChange={(e) => setOTP(e.value)} integerOnly />
              </div>
              <div>
                <div className="badge bg-danger-transparent mb-3">
                  <p className="d-flex align-items-center mb-0">
                    <i className="ti ti-clock me-1" />
                    {formatTime(timer)}
                  </p>
                </div>
                <div className="mb-3">
                  {
                    <div className="text-center" style={{height:12}}>
                      {
                        errorMessage
                        ?
                          <>
                            <span className="link-danger">
                            <i className="ti ti-alert-triangle" /> {errorMessage}
                            </span>
                          </>
                        :
                        resendMessage
                        ?
                          <>
                            <span className="link-success">
                            <i className="ti ti-check-circle" /> {resendMessage}
                            </span>
                          </>
                        : <></>
                      }
                    </div>
                  }
                </div>
                <div className="mb-3 d-flex justify-content-center">
                  <p className="text-gray-9">
                    Didn't receive OTP?{" "}
                    <Link
                      to="#"
                      className={`text-primary ${isResendDisabled ? 'disabled-link' : ''}`}
                      onClick={handleResendOTP}
                      style={{
                        pointerEvents: isResendDisabled ? 'none' : 'auto',
                        opacity: isResendDisabled ? 0.6 : 1,
                        textDecoration: 'none'
                      }}
                    >
                      Resend OTP
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <button
                type="submit"
                onClick={handleVerifyOTP}
                className="btn btn-primary w-100"
              >
                Verify OTP
              </button>
            </div>
            <div className="mb-3">
              <div className="text-center">
                <Link
                  to="#"
                  onClick={toggleLoginMethod}
                  className="text-primary"
                  style={{ textDecoration: 'none' }}
                >
                  <i className="ti ti-arrow-left me-1" />
                  Back to Login with Password
                </Link>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  const renderLoginWithPassword = () => {
    return (
      <>
      <div className="mb-3">
        <label className="form-label">Username</label>
        <div className="input-group">
          <input
            type="text"
            defaultValue=""
            placeholder="Enter your username"
            className="form-control border-end-0"
            onChange={(e)=>setUserName(e.target.value)} value={username}
          />
          <span className="input-group-text border-start-0">
            <i className="ti ti-user" />
          </span>
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label">Password</label>
        <div className="pass-group">
        <input
            type={
              passwordVisibility.password
                ? "text"
                : "password"
            }
            placeholder="Enter your password"
            className="pass-input form-control"
            onChange={(e)=>setPassword(e.target.value)} value={password}
          />
          <span
            className={`ti toggle-passwords ${passwordVisibility.password
              ? "ti-eye"
              : "ti-eye-off"
              }`}
            onClick={() =>
              togglePasswordVisibility("password")
            }
          ></span>
        </div>
      </div>
      <div className="mb-3">
        {
        <div className="text-center" style={{height:12}}>
          {
            errorMessage
            ?
              <>
                <span className="link-danger">
                <i className="ti ti-alert-triangle" /> {errorMessage}
                </span>
              </>
            :
              <></>
            }
          </div>
        
        }
      </div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <div className="form-check form-check-md mb-0">
            <input
              className="form-check-input"
              id="remember_me"
              type="checkbox"
              onChange={(e:any) => {
                setRememberMe(e.target.checked)
              }}
              defaultChecked={rememberMe}
            />
            <label
              htmlFor="remember_me"
              className="form-check-label mt-0"
            >
              Remember Me
            </label>
          </div>
        </div>
        <div className="text-end">
          <Link to={all_routes.forgotPassword} className="hover-a">
            Forgot Password?
          </Link>
        </div>
      </div>
      <div className="mb-3">
      <button
          onClick={handleSubmit}
          type="submit"
          className="btn btn-primary w-100"
        >
          Sign In
        </button>
      </div>
      <div className="mb-3">
        <div className="text-center">
          <span className="text-gray-9">Or </span>
          <Link
            to="#"
            onClick={toggleLoginMethod}
            className="text-primary"
            style={{ textDecoration: 'none' }}
          >
            Login with OTP
          </Link>
        </div>
      </div>
    </>
    )
  }

  return (
    <div className="container-fuild"> 
      <PreLoader showLoader={inProgress} />
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-5">
            <div className="login-background position-relative d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100">
              <div className="authentication-card w-100">
                <div className="authen-overlay-item border w-100">
                  {/* <h1 className="text-white display-1">
                    Centralized control <br />  for seamless school <br /> management.
                  </h1> */}
                  <div className="my-4 mx-auto authen-overlay-img">
                    <ImageWithBasePath src="assets/img/bg/authentication-bg-02.svg" alt="Img" />
                  </div>
                  {/* <div>
                    <p className="text-white fs-20 fw-semibold text-center">
                      Efficiently manage your workforce, streamline <br />{" "}
                      operations effortlessly.
                    </p>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap">
              <div className="col-md-7 mx-auto vh-100">
                <form className="vh-100">
                  <div className="vh-100 d-flex flex-column justify-content-between p-4 pb-0">
                    <div className=" mx-auto mb-5 text-center">
                      <HeaderLogo 
                        url={tenantInfo?.institutionLogo}
                        name={tenantInfo?.institutionName}
                        size="L"
                      />
                    </div>
                    <div className="">
                      <div className="text-center mb-3">
                        <h2 className="mb-2">{isOTPLogin ? "Login with OTP" : "Sign In"}</h2>
                        <p className="mb-0">
                          {isOTPLogin 
                            ? "Enter your username to receive OTP" 
                            : "Please enter your details to sign in"}
                        </p>
                      </div>
                      <div className="mb-3" hidden={hideTenantInput}>
                        <label className="form-label">Tenant ID</label>
                        <div className="input-group">
                          <input
                            type="text"
                            defaultValue=""
                            placeholder="Tenant ID"
                            className="form-control border-end-0"
                            onChange={(e)=>setTenant(e.target.value)} value={tenant}
                          />
                          <span className="input-group-text border-start-0">
                            <i className="ti ti-building" />
                          </span>
                        </div>
                      </div>
                      {isOTPLogin ? renderLoginWithOTP() : renderLoginWithPassword()}
                      {!isOTPLogin && (
                        <div className="text-center">
                          <h6 className="fw-normal text-dark mb-0">
                            Don't have an account?
                            <Link to={all_routes.register} className="hover-a">
                              {" "}
                              Create Account
                            </Link>
                          </h6>
                        </div>
                      )}
                    </div>
                    <div className="mt-5 pb-4 text-center">
                      <CopyRightInfo/>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    
    </div>

  );
};

export default Login;

